import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Grid, TextField, FormControl, InputLabel, Select, MenuItem,
  Typography, Box, Chip, Autocomplete,
  FormControlLabel, Switch, Alert, CircularProgress
} from '@mui/material';
// import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers/DatePicker';
// import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
// import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import api from '../utils/api';

interface Department {
  id: number;
  name: string;
  description: string;
  is_active: boolean;
}

interface Location {
  id: number;
  name: string;
  address: string;
  is_active: boolean;
}

interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  department: Department | null;
  role: string;
  location: Location | null;
}

interface DocumentAssignmentDialogProps {
  open: boolean;
  onClose: () => void;
  documentId: number;
  documentTitle: string;
  onAssignmentCreated: () => void;
}

interface AssignmentFormData {
  assignment_type: string;
  assigned_user_id: number | null;
  assigned_department: string;
  assigned_role: string;
  due_date: Date | null;
  notes: string;
  send_email_notification: boolean;
  send_reminder: boolean;
  reminder_days: number;
}

const DocumentAssignmentDialog: React.FC<DocumentAssignmentDialogProps> = ({
  open,
  onClose,
  documentId,
  documentTitle,
  onAssignmentCreated
}) => {
  const [users, setUsers] = useState<User[]>([]);
  const [departments, setDepartments] = useState<string[]>([]);
  const [roles, setRoles] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState<AssignmentFormData>({
    assignment_type: 'individual',
    assigned_user_id: null,
    assigned_department: '',
    assigned_role: '',
    due_date: null,
    notes: '',
    send_email_notification: true,
    send_reminder: true,
    reminder_days: 7
  });

  useEffect(() => {
    if (open) {
      fetchUsers();
      fetchDepartments();
      fetchRoles();
    }
  }, [open]);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users/');
      setUsers(response.data.results || response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await api.get('/users/');
      const userData = response.data.results || response.data;
      const departmentSet = new Set(
        userData
          .map((user: User) => user.department?.name)
          .filter(Boolean)
      );
      const uniqueDepartments = Array.from(departmentSet) as string[];
      setDepartments(uniqueDepartments);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await api.get('/users/');
      const userData = response.data.results || response.data;
      const roleSet = new Set(userData.map((user: User) => user.role).filter(Boolean));
      const uniqueRoles = Array.from(roleSet) as string[];
      setRoles(uniqueRoles);
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const handleAssignmentTypeChange = (type: string) => {
    setFormData(prev => ({
      ...prev,
      assignment_type: type,
      assigned_user_id: null,
      assigned_department: '',
      assigned_role: ''
    }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      // Validate required fields
      if (formData.assignment_type === 'individual' && !formData.assigned_user_id) {
        setError('Please select a user for individual assignment');
        return;
      }
      if (formData.assignment_type === 'department' && !formData.assigned_department) {
        setError('Please select a department');
        return;
      }
      if (formData.assignment_type === 'role' && !formData.assigned_role) {
        setError('Please select a role');
        return;
      }

      // Get list of assignee IDs based on assignment type
      let assignee_ids: number[] = [];
      if (formData.assignment_type === 'individual' && formData.assigned_user_id) {
        assignee_ids = [formData.assigned_user_id];
      } else if (formData.assignment_type === 'department' && formData.assigned_department) {
        assignee_ids = users
          .filter(user => user.department?.name === formData.assigned_department)
          .map(user => user.id);
      } else if (formData.assignment_type === 'role' && formData.assigned_role) {
        assignee_ids = users
          .filter(user => user.role === formData.assigned_role)
          .map(user => user.id);
      } else if (formData.assignment_type === 'all_employees') {
        assignee_ids = users.map(user => user.id);
      }

      if (assignee_ids.length === 0) {
        setError('No users found matching the selected criteria');
        return;
      }

      // Use the unified Assignment API format
      const assignmentData = {
        assignment_type: 'document',
        content_type_str: 'document',  // Use string format instead of ContentType ID
        object_id: documentId,
        assignee_ids: assignee_ids,
        due_date: formData.due_date ? formData.due_date.toISOString().split('T')[0] : null,
        notes: formData.notes,
        send_reminders: formData.send_reminder,
        include_in_digest: formData.send_email_notification,
        reminder_frequency_days: formData.reminder_days,
        completion_criteria: 'all',
        is_active: true
      };

      await api.post('/assignments/', assignmentData);
      
      onAssignmentCreated();
      onClose();
      
      // Reset form
      setFormData({
        assignment_type: 'individual',
        assigned_user_id: null,
        assigned_department: '',
        assigned_role: '',
        due_date: null,
        notes: '',
        send_email_notification: true,
        send_reminder: true,
        reminder_days: 7
      });

    } catch (error: any) {
      setError(error.response?.data?.detail || 'Failed to create assignment');
      console.error('Error creating assignment:', error);
    } finally {
      setLoading(false);
    }
  };

  const getAssignmentTargetCount = () => {
    switch (formData.assignment_type) {
      case 'individual':
        return formData.assigned_user_id ? 1 : 0;
      case 'department':
        return formData.assigned_department 
          ? users.filter(user => user.department?.name === formData.assigned_department).length 
          : 0;
      case 'role':
        return formData.assigned_role 
          ? users.filter(user => user.role === formData.assigned_role).length 
          : 0;
      case 'all_employees':
        return users.length;
      default:
        return 0;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          Assign Document: {documentTitle}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* Assignment Type */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Assignment Type</InputLabel>
                <Select
                  value={formData.assignment_type}
                  label="Assignment Type"
                  onChange={(e) => handleAssignmentTypeChange(e.target.value)}
                >
                  <MenuItem value="individual">Individual User</MenuItem>
                  <MenuItem value="department">Department</MenuItem>
                  <MenuItem value="role">Role</MenuItem>
                  <MenuItem value="all_employees">All Employees</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Individual User Selection */}
            {formData.assignment_type === 'individual' && (
              <Grid item xs={12}>
                <Autocomplete
                  options={users}
                  getOptionLabel={(option) => `${option.first_name} ${option.last_name} (${option.email})`}
                  value={users.find(user => user.id === formData.assigned_user_id) || null}
                  onChange={(_, value) => setFormData(prev => ({ ...prev, assigned_user_id: value?.id || null }))}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select User"
                      fullWidth
                      required
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Box>
                        <Typography variant="body1">
                          {option.first_name} {option.last_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {option.email} • {option.department?.name || 'No Department'} • {option.role}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                />
              </Grid>
            )}

            {/* Department Selection */}
            {formData.assignment_type === 'department' && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Department</InputLabel>
                  <Select
                    value={formData.assigned_department}
                    label="Department"
                    onChange={(e) => setFormData(prev => ({ ...prev, assigned_department: e.target.value }))}
                    required
                  >
                    {departments.map((dept) => (
                      <MenuItem key={dept} value={dept}>
                        {dept} ({users.filter(user => user.department?.name === dept).length} users)
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* Role Selection */}
            {formData.assignment_type === 'role' && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={formData.assigned_role}
                    label="Role"
                    onChange={(e) => setFormData(prev => ({ ...prev, assigned_role: e.target.value }))}
                    required
                  >
                    {roles.map((role) => (
                      <MenuItem key={role} value={role}>
                        {role} ({users.filter(user => user.role === role).length} users)
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* Assignment Summary */}
            <Grid item xs={12}>
              <Box sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Assignment Summary:
                </Typography>
                <Typography variant="body1">
                  This document will be assigned to <strong>{getAssignmentTargetCount()}</strong> user(s)
                </Typography>
              </Box>
            </Grid>

            {/* Due Date */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Due Date (Optional)"
                type="date"
                value={formData.due_date ? formData.due_date.toISOString().split('T')[0] : ''}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  due_date: e.target.value ? new Date(e.target.value) : null 
                }))}
                InputLabelProps={{ shrink: true }}
                helperText="Leave empty for no due date"
              />
            </Grid>

            {/* Reminder Days */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Reminder Frequency (days)"
                type="number"
                value={formData.reminder_days}
                onChange={(e) => setFormData(prev => ({ ...prev, reminder_days: parseInt(e.target.value) || 7 }))}
                helperText="Days between reminders in the daily digest"
                inputProps={{ min: 1, max: 30 }}
              />
            </Grid>

            {/* Notes */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Assignment Notes"
                multiline
                rows={3}
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Optional notes about this assignment..."
              />
            </Grid>

            {/* Notification Settings */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Notification Settings
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.send_email_notification}
                    onChange={(e) => setFormData(prev => ({ ...prev, send_email_notification: e.target.checked }))}
                  />
                }
                label="Include in daily digest"
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 6, mt: -0.5, mb: 1 }}>
                Assignees are notified in the weekday daily digest, including assignments created that day. Assigning does not send a separate email.
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.send_reminder}
                    onChange={(e) => setFormData(prev => ({ ...prev, send_reminder: e.target.checked }))}
                  />
                }
                label="Send reminder notifications"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Creating...' : 'Create Assignment'}
          </Button>
        </DialogActions>
      </Dialog>
  );
};

export default DocumentAssignmentDialog;